import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  addDays, addMonths, eachDayOfInterval, endOfDay, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, startOfDay, startOfMonth, startOfWeek,
} from 'date-fns';
import type { CalendarEvent } from '@wrapforce/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useLocationStore, ALL_LOCATIONS } from '@/stores/location';
import { useCalendar } from './api';
import { Sheet } from '@/components/ui/sheet';
import { cn } from '@/lib/cn';
import { useLocaleFormatters } from '@/lib/format';
import { colorForJob } from './colors';
import { DayTimeline } from './DayTimeline';

const STATUS_CHIP: Record<string, string> = {
  unscheduled: 'bg-surface-2 text-muted', scheduled: 'bg-info/15 text-info', in_progress: 'bg-accent/15 text-accent-ink',
  completed: 'bg-success/15 text-success', closed: 'bg-surface-2 text-muted', cancelled: 'bg-danger/15 text-danger',
};

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const VIEW_MODES = ['day', 'week', 'twoWeek', 'month'] as const;
type ViewMode = (typeof VIEW_MODES)[number];

function EventDetailCard({ event }: { event: CalendarEvent }) {
  const { t } = useTranslation('calendar');
  const { formatDate } = useLocaleFormatters();
  const color = colorForJob(event.jobId);
  return (
    <Link
      to={`/jobs/${event.jobId}`}
      className={cn('block rounded-[var(--radius-input)] border border-l-4 border-border p-3 hover:border-accent', color.border)}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-muted">{formatDate(new Date(event.scheduledStart), 'h:mm a')}</span>
        <span className={cn('px-2 py-0.5 text-[10px] font-bold uppercase', STATUS_CHIP[event.status])}>{t(`status.${event.status}`)}</span>
      </div>
      <div className="mt-1 font-semibold text-ink">{event.title}</div>
      <div className="mt-2 text-xs text-muted">
        <span className="font-semibold text-ink">{t('detail.assignedTo')}: </span>
        {event.installerNames.length ? event.installerNames.join(', ') : t('detail.unassigned')}
      </div>
      <div className="mt-1 text-xs text-muted">
        <span className="font-semibold text-ink">{t('detail.customer')}: </span>
        {event.customerName}
        {event.customerEmail ? ` · ${event.customerEmail}` : ''}
        {event.customerPhone ? ` · ${event.customerPhone}` : ''}
      </div>
    </Link>
  );
}

export function CalendarPage() {
  const { t } = useTranslation('calendar');
  const { formatDate } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const activeLocationId = useLocationStore((s) => s.activeLocationId);
  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;
  const locationId = gateOn && activeLocationId !== ALL_LOCATIONS ? activeLocationId : undefined;
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [cursor, setCursor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const range = useMemo(() => {
    if (viewMode === 'day') return { start: startOfDay(cursor), end: endOfDay(cursor) };
    if (viewMode === 'week') return { start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) };
    if (viewMode === 'twoWeek') {
      const start = startOfWeek(cursor, { weekStartsOn: 1 });
      return { start, end: endOfDay(addDays(start, 13)) };
    }
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    return { start: startOfWeek(monthStart, { weekStartsOn: 1 }), end: endOfWeek(monthEnd, { weekStartsOn: 1 }) };
  }, [viewMode, cursor]);

  const days = useMemo(() => eachDayOfInterval({ start: range.start, end: range.end }), [range]);

  const { data: events = [] } = useCalendar(workspaceId, range.start.toISOString(), range.end.toISOString(), locationId);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const key = format(new Date(e.scheduledStart), 'yyyy-MM-dd');
      map.set(key, [...(map.get(key) ?? []), e]);
    }
    return map;
  }, [events]);

  const selectedEvents = selectedDay ? eventsByDay.get(format(selectedDay, 'yyyy-MM-dd')) ?? [] : [];

  function step(dir: 1 | -1) {
    if (viewMode === 'day') setCursor((c) => addDays(c, dir));
    else if (viewMode === 'week') setCursor((c) => addDays(c, dir * 7));
    else if (viewMode === 'twoWeek') setCursor((c) => addDays(c, dir * 14));
    else setCursor((c) => addMonths(c, dir));
  }

  const title = viewMode === 'day'
    ? formatDate(cursor, 'EEEE, MMMM d')
    : viewMode === 'month'
      ? formatDate(cursor, 'MMMM yyyy')
      : `${formatDate(range.start, 'MMM d')} - ${formatDate(range.end, 'MMM d, yyyy')}`;

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 border border-border p-0.5">
          {VIEW_MODES.map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              className={cn('px-2.5 py-1 text-xs font-semibold uppercase tracking-wide', viewMode === m ? 'bg-ink text-surface' : 'text-muted hover:bg-surface-2')}
            >
              {t(`view.${m}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface">
        <div className="flex items-center gap-2 border-b-2 border-ink px-4 py-3">
          <span className="font-display text-sm font-bold uppercase tracking-wide">{title}</span>
          <div className="ml-auto flex gap-1.5">
            <button onClick={() => step(-1)} className="grid h-7 w-7 place-items-center border border-border text-muted hover:bg-surface-2">
              <ChevronLeft size={14} />
            </button>
            <button onClick={() => setCursor(new Date())} className="border border-border px-2.5 text-xs font-semibold text-muted hover:bg-surface-2">
              {t('today')}
            </button>
            <button onClick={() => step(1)} className="grid h-7 w-7 place-items-center border border-border text-muted hover:bg-surface-2">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {viewMode === 'day' ? (
          <div className="p-3">
            <DayTimeline day={cursor} events={events} onSelectEvent={setSelectedEvent} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 border-b border-border">
              {WEEKDAY_KEYS.map((d) => (
                <div key={d} className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted">
                  {t(`weekday.${d}`)}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px bg-border">
              {days.map((day) => {
                const dayEvents = eventsByDay.get(format(day, 'yyyy-MM-dd')) ?? [];
                const inMonth = viewMode !== 'month' || isSameMonth(day, cursor);
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDay(day)}
                    className={cn(
                      'flex min-h-[86px] flex-col gap-1 p-1.5 text-left',
                      inMonth ? 'bg-surface' : 'bg-surface-2/50',
                      selectedDay && isSameDay(day, selectedDay) ? 'ring-2 ring-accent ring-inset' : '',
                    )}
                  >
                    <span className={cn('grid h-5 w-5 place-items-center font-mono text-[11px]', isToday(day) ? 'bg-ink text-surface' : 'text-muted')}>
                      {formatDate(day, 'd')}
                    </span>
                    {dayEvents.slice(0, 3).map((e) => {
                      const color = colorForJob(e.jobId);
                      return (
                        <span key={`${e.jobId}-${e.scheduledStart}`} className={cn('truncate px-1.5 py-0.5 text-[10px] font-semibold', color.bg, color.text)}>
                          {formatDate(new Date(e.scheduledStart), 'HH:mm')} {e.title}
                        </span>
                      );
                    })}
                    {dayEvents.length > 3 ? <span className="text-[10px] text-muted">{t('moreEvents', { count: dayEvents.length - 3 })}</span> : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {selectedDay ? (
        <Sheet open onOpenChange={(v) => !v && setSelectedDay(null)} title={formatDate(selectedDay, 'EEEE, MMMM d')}>
          <div className="flex flex-col gap-3">
            {selectedEvents.length === 0 ? <p className="text-sm text-muted">{t('empty.noJobs')}</p> : null}
            {selectedEvents.map((e) => (
              <EventDetailCard key={`${e.jobId}-${e.scheduledStart}`} event={e} />
            ))}
          </div>
        </Sheet>
      ) : null}

      {selectedEvent ? (
        <Sheet open onOpenChange={(v) => !v && setSelectedEvent(null)} title={formatDate(new Date(selectedEvent.scheduledStart), 'EEEE, MMMM d')}>
          <EventDetailCard event={selectedEvent} />
        </Sheet>
      ) : null}
    </div>
  );
}
